import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import usePage from '../hooks/usePage';
import { transformProductsData } from '../utils/dataTransformer';
import './ServicePage.css';

const MANUFACTURING_SLUG = 'home';

const StickyImagePanel = ({ activeIndex, slides, isPanelVisible, style }) => {
  const [displayIndex, setDisplayIndex] = useState(activeIndex);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (activeIndex === displayIndex) return;
    setIsAnimating(true);
    const animationTimer = setTimeout(() => {
      setDisplayIndex(activeIndex);
      setIsAnimating(false);
    }, 220);
    return () => clearTimeout(animationTimer);
  }, [activeIndex, displayIndex]);

  const activeSlide = slides[displayIndex];
  if (!activeSlide) return null;

  return (
    <div
      className={`service-fixed-panel ${isPanelVisible ? 'is-visible' : 'is-hidden'}`}
      style={style}
    >
      <div className={`service-sticky-img-wrap ${isAnimating ? 'is-fading' : 'is-visible'}`}>
        <div className="service-item__glow-ring" />
        <div className="service-item__img-box">
          {activeSlide.imageUrl ? (
            <img
              src={activeSlide.imageUrl}
              alt={activeSlide.imageAlt || activeSlide.title}
              className="service-item__img"
            />
          ) : (
            <div className="service-item__img-placeholder">
              <svg width="100%" height="100%" viewBox="0 0 260 260" fill="none" opacity="0.25">
                <rect x="20" y="20" width="220" height="220" rx="4" stroke="#77a533" strokeWidth="1" />
                {[60, 100, 140, 180].map((v, i) => (
                  <line key={`v${i}`} x1={v} y1="20" x2={v} y2="240" stroke="#77a533" strokeWidth="0.5" />
                ))}
                {[60, 100, 140, 180].map((v, i) => (
                  <line key={`h${i}`} x1="20" y1={v} x2="240" y2={v} stroke="#77a533" strokeWidth="0.5" />
                ))}
                {[[60, 60], [100, 100], [140, 60], [180, 140], [60, 180], [180, 60]].map(([cx, cy], i) => (
                  <circle key={i} cx={cx} cy={cy} r="4" fill="#77a533" opacity="0.6" />
                ))}
                <rect x="110" y="100" width="40" height="24" rx="3" stroke="#77a533" strokeWidth="1" fill="rgba(119,165,51,0.1)" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ManufacturingPage = () => {
  const { page, loading, error } = usePage(MANUFACTURING_SLUG);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const itemRefs = useRef([]);
  const sectionRef = useRef(null);

  const sections = page?.acf?.product_groups
    ? transformProductsData(page.acf.product_groups).filter((section) => /manufactur/i.test(section.label || section.name || ''))
    : [];

  const slides = sections.flatMap((section) => section.slides);

  const handleScroll = useCallback(() => {
    const refs = itemRefs.current;
    const sectionEl = sectionRef.current;
    if (!refs.length || !sectionEl) return;

    const sectionRect = sectionEl.getBoundingClientRect();
    const sectionTop = sectionRect.top;
    const sectionBottom = sectionRect.bottom;
    const NAVBAR_HEIGHT = 80;

    const lastItemEl = refs[refs.length - 1];
    const lastItemBottom = lastItemEl
      ? lastItemEl.getBoundingClientRect().bottom
      : sectionBottom;
    const isLastItemGone = lastItemBottom < window.innerHeight * 0.5;

    const shouldShowPanel =
      sectionTop < window.innerHeight &&
      sectionBottom > NAVBAR_HEIGHT &&
      !isLastItemGone;

    setIsPanelVisible(shouldShowPanel);

    const viewportMidPoint = window.innerHeight * 0.4;
    let bestIndex = 0;
    let bestDistance = Infinity;

    refs.forEach((el, idx) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distance = Math.abs(elementCenter - viewportMidPoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = idx;
      }
    });

    const scrollBottom = window.innerHeight + window.scrollY;
    const pageHeight = document.documentElement.scrollHeight;
    if (pageHeight - scrollBottom < 300) {
      bestIndex = refs.length - 1;
    }

    setActiveIndex(bestIndex);
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [slides.length, handleScroll]);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading__spinner" />
      </div>
    );
  }

  if (error || slides.length === 0) {
    return (
      <div className="error-state">
        <div className="error-state__icon">
          <svg viewBox="0 0 48 48" fill="none" width="48" height="48">
            <circle cx="24" cy="24" r="22" stroke="var(--green-dark)" strokeWidth="2" opacity="0.4" />
            <path d="M24 14v12" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="24" cy="32" r="1.5" fill="var(--green)" />
          </svg>
        </div>
        <h2 className="error-state__title">Something went wrong</h2>
        <p className="error-state__message">
          {error || 'No manufacturing content available at the moment.'}
        </p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="service-page">
      <header className="product-header">
        <div className="header-container">
          <h1 className="service-hero__title">Manufacturing</h1>
          <p className="service-hero__desc">
            We deliver manufacturing solutions that combine precision engineering, reliable automation,
            and scalable systems for modern industrial operations.
          </p>
        </div>
      </header>

      <main className="page-main">
        <div className="container">
          <div className="service-scroll-layout" ref={sectionRef}>
            <div className="service-scroll-text">
              {slides.map((slide, idx) => (
                <div
                  key={slide.slug || idx}
                  className={`service-item ${activeIndex === idx ? 'is-active' : ''}`}
                  ref={(el) => (itemRefs.current[idx] = el)}
                >
                  <div className="service-item__text">
                    <h2 className="service-item__title">{slide.title}</h2>
                    <div className="service-item__line" />

                    {slide.imageUrl && (
                      <div className="service-item__mobile-img">
                        <img src={slide.imageUrl} alt={slide.imageAlt || slide.title} />
                      </div>
                    )}

                    <p className="service-item__desc">{slide.description}</p>
                    <Link to={`/product/${slide.slug}`} className="service-item__explore">
                      Explore
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M3 8h10M9 4l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <StickyImagePanel
              activeIndex={activeIndex}
              slides={slides}
              isPanelVisible={isPanelVisible}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManufacturingPage;